// @file Helper for whiteboard upload
import { fallbackAttachmentSizeMapping, getFallbackAttachmentType } from '@@/bits/attachments'
import { readImageFileData } from '@@/bits/file_handler'
import { isAppUsing } from '@@/bits/flip'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { CANVAS_HEIGHT, CANVAS_WIDTH, getCanvasDimensions } from '@@/bits/whiteboard'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import type { Post } from '@@/types'
import type { Link } from '@padlet/arvo'
import { getDisplayAttributes, LinkHelpers } from '@padlet/beethoven-client'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { TLShapeId } from 'tldraw'

function throwErrorForMissingDataInNewestPost(): void {
  throw new Error('Missing data in newest post')
}

const DEFAULT_ASPECT_RATIO = 11 / 6
const DEFAULT_WIDTH = 220
const LARGE_CONTENT_WIDTH = 600
const MEDIUM_CONTENT_WIDTH = 400

const MAXIMUM_NUMBER_OF_FILES = 12
const DEFAULT_POSITION = {
  x: CANVAS_WIDTH / 2 - CANVAS_WIDTH / 3 / 2,
  y: CANVAS_HEIGHT / 2 - CANVAS_HEIGHT / 3 / 2,
}
const HEIGHT_LIMIT_FACTOR = 0.8

const AUDIO_PLAYER_WITH_IMAGE_WIDTH = 260
const AUDIO_PLAYER_WITHOUT_IMAGE_WIDTH = 236
const AUDIO_PLAYER_NO_IMAGE_VISUALIZER_ASPECT_RATIO = AUDIO_PLAYER_WITH_IMAGE_WIDTH / 98

/**
 * Analyzes the provided link, file, or shape to determine an appropriate
 * default width for the uploading shape. It categorizes content into 4 groups:
 * 1. Audio player v2: 236px or 260px, depending on whether the audio has cover art image or not
 * 2. Large content (600px): Photos, videos
 * 3. Medium content (400px): Audio, PDFs, web pages, documents
 * 4. Default content (220px): All other types
 *
 * @param {Object} params - The parameters for the function.
 * @param {Link} [params.link] - The link object, if available.
 * @param {File} [params.file] - The file object, if available.
 * @returns {number} The default width for the uploading shape.
 */
function getDefaultWidthForUploadingShape({
  link,
  file,
  shape,
}: {
  link?: Link
  file?: File
  shape?: PadletAttachmentShape
}): number {
  if (link != null) {
    if (
      isAppUsing('audioPlayerV2Sandbox') &&
      LinkHelpers.isUploadedAudio(link) &&
      shape?.meta.audioPlayerVersion === 2
    ) {
      const hasCoverArt = link.preview_image != null
      return hasCoverArt ? AUDIO_PLAYER_WITH_IMAGE_WIDTH : AUDIO_PLAYER_WITHOUT_IMAGE_WIDTH
    }

    if (LinkHelpers.isPhoto(link) || LinkHelpers.isVideo(link)) {
      return LARGE_CONTENT_WIDTH
    }
    if (
      LinkHelpers.isAudio(link) ||
      LinkHelpers.isPdf(link) ||
      LinkHelpers.isPage(link) ||
      LinkHelpers.isDocument(link)
    ) {
      return MEDIUM_CONTENT_WIDTH
    }
  }

  if (file != null) {
    const fileType = file.type.toLowerCase()
    if (fileType.startsWith('image/') || fileType.startsWith('video/')) {
      return LARGE_CONTENT_WIDTH
    }
    if (fileType.startsWith('audio/') || fileType === 'application/pdf' || fileType.startsWith('text/')) {
      return MEDIUM_CONTENT_WIDTH
    }
  }

  return DEFAULT_WIDTH
}

function getDefaultAspectRatioForUploadingShape({ link, shape }: { link: Link; shape: PadletAttachmentShape }): number {
  // Audio shape without cover art uses a fixed aspect ratio
  const hasCoverArt = link.preview_image != null
  if (
    isAppUsing('audioPlayerV2Sandbox') &&
    LinkHelpers.isUploadedAudio(link) &&
    shape.meta.audioPlayerVersion === 2 &&
    !hasCoverArt
  ) {
    return AUDIO_PLAYER_NO_IMAGE_VISUALIZER_ASPECT_RATIO
  }

  const { width: canvasWidth } = getCanvasDimensions()
  const attributes = getDisplayAttributes(link, { width: canvasWidth / 2, use_original_image_dimensions: true })
  let aspectRatio = attributes.image_aspect_ratio

  // Handle special cases for aspect ratio
  if (attributes.is_fallback) {
    const fallbackType = getFallbackAttachmentType(link)
    const { width, height } = fallbackAttachmentSizeMapping[fallbackType]
    aspectRatio = width / height
  } else if (link?.provider_name?.toLowerCase() === 'youtube') {
    aspectRatio = 16 / 9
  }

  return aspectRatio
}

async function calculateFileDimensions({
  file,
  canvasHeight = CANVAS_HEIGHT,
}: {
  file: File
  canvasHeight?: number
}): Promise<{ width: number; height: number; aspectRatio: number }> {
  const defaultWidth = getDefaultWidthForUploadingShape({ file })
  let width = defaultWidth
  let height = defaultWidth / DEFAULT_ASPECT_RATIO
  let aspectRatio = DEFAULT_ASPECT_RATIO

  if (file.type.startsWith('image/')) {
    const imageFileData = await readImageFileData(file)
    if (imageFileData != null) {
      aspectRatio = imageFileData.width / imageFileData.height
      width = defaultWidth
      height = width / aspectRatio
      // The height can grow to be bigger than the canvas height
      // We limit the height to 80% of the canvas height
      if (height > canvasHeight) {
        height = canvasHeight * HEIGHT_LIMIT_FACTOR
        width = height * aspectRatio
      }
    }
  }
  return { width, height, aspectRatio }
}

async function calculateUploadedFilePositionsAndDimensions({
  files,
  dropPosition = DEFAULT_POSITION,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
}: {
  files: File[]
  dropPosition?: { x: number; y: number }
  canvasWidth?: number
  canvasHeight?: number
}): Promise<Array<{ file: File; width: number; height: number; aspectRatio: number; x: number; y: number }>> {
  const spaceBetweenShapesInStack = 12
  // Determine stacking direction based on drop position
  // Files should be laid out inwards from the drop position
  const xDirection = dropPosition.x < canvasWidth / 2 ? 1 : -1
  const yDirection = dropPosition.y < canvasHeight / 2 ? 1 : -1

  // Calculate positions and dimensions for all files
  return await Promise.all(
    files.map(async (file, index) => {
      const { width, height, aspectRatio } = await calculateFileDimensions({ file, canvasHeight })

      // Calculate position for each shape
      const x = dropPosition.x + (xDirection === -1 ? -width : 0) + xDirection * index * spaceBetweenShapesInStack
      const y = dropPosition.y + (yDirection === -1 ? -height : 0) + yDirection * index * spaceBetweenShapesInStack

      return { file, width, height, aspectRatio, x, y }
    }),
  )
}

/**
 * Constrains the position of an PadletAttachmentShape within the canvas boundaries.
 * This function ensures that the shape remains visible on the canvas.
 *
 * @param {PadletAttachmentShape} shape - The shape to be constrained.
 * @param {Object} newDimensions - The new dimensions of the shape.
 * @param {number} [newDimensions.w] - The new width of the shape.
 * @param {number} [newDimensions.h] - The new height of the shape.
 * @returns {Object} An object containing the new x and y coordinates for the shape.
 * @property {number} newX - The new x-coordinate for the shape.
 * @property {number} newY - The new y-coordinate for the shape.
 */
function constrainShapePositionToCanvas(
  shape: PadletAttachmentShape,
  newDimensions: { w?: number; h?: number },
): { newX: number; newY: number } {
  // If the shape or the new dimensions are not set, return the current position
  if (shape.props.w == null || shape.props.h == null || newDimensions.w == null || newDimensions.h == null) {
    return { newX: shape.x, newY: shape.y }
  }

  const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions()
  // Calculate new position to keep the shape within the viewport
  let newX = shape.x
  let newY = shape.y
  const currentWidth = shape.props.w
  const currentHeight = shape.props.h
  const newWidth: number = newDimensions.w
  const newHeight: number = newDimensions.h
  // Calculate current paddings
  const paddingLeft = shape.x
  const paddingTop = shape.y
  const paddingRight = canvasWidth - (shape.x + currentWidth)
  const paddingBottom = canvasHeight - (shape.y + currentHeight)

  // Adjust x-coordinate
  if (newX + newWidth > CANVAS_WIDTH) {
    // If expanding beyond right edge, maintain right padding
    newX = CANVAS_WIDTH - newWidth - paddingRight
  } else if (newX + newWidth < shape.x + currentWidth) {
    // If shrinking, maintain left padding
    newX = paddingLeft
  }

  // Adjust y-coordinate
  if (newY + newHeight > CANVAS_HEIGHT) {
    // If expanding beyond bottom edge, maintain bottom padding
    newY = CANVAS_HEIGHT - newHeight - paddingBottom
  } else if (newY + newHeight < shape.y + currentHeight) {
    // If shrinking, maintain top padding
    newY = paddingTop
  }
  return { newX, newY }
}

/**
 * Deletes failed uploads from the editor.
 *
 * This function performs two types of deletions:
 * 1. Deletes shapes that are not uploaded and visible to the current user.
 *    This covers the case where the same user opens another tab while a shape is uploading,
 *    ensuring it doesn't delete shapes they cannot see.
 * 2. Deletes shapes that are not uploaded and were created over 24 hours ago.
 *    This is a cleanup operation for the store in case the user never opens that tab again.
 */
function deleteFailedUploads(): void {
  const editor = getEditorObject()
  if (editor == null) return

  const failedUploads = editor.store
    .allRecords()
    .filter(
      (record) =>
        record.typeName === 'shape' && record?.type === 'padlet-attachment-shape' && record.meta.isUploaded === false,
    )

  const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000

  failedUploads.forEach((shape) => {
    const isViewableByUser = useWhiteboardPermissionsStore().canIViewShape(shape)
    const isOlderThanOneDay = typeof shape.meta.ca === 'number' && Date.now() - shape.meta.ca > ONE_DAY_IN_MS

    if (isViewableByUser || isOlderThanOneDay) {
      editor.deleteShape(shape.id as TLShapeId)
    }
  })
}

async function calculateShapeDimensions(
  post: Post,
  shape: PadletAttachmentShape,
): Promise<{ width: number; height: number } | null> {
  const url: string = post.wish_content?.attachment_props?.url ?? post.attachment
  const link = await useSurfaceAttachmentsStore().fetchAndStoreLinkAttributes(url)
  if (link == null) {
    throwErrorForMissingDataInNewestPost()
    return null
  }

  const shortSide = getDefaultWidthForUploadingShape({ link, shape })
  let width: number, height: number

  const aspectRatio = getDefaultAspectRatioForUploadingShape({ link, shape })

  // Depending on the aspect ratio, set the width/height as the short side
  if (aspectRatio > 1) {
    height = shortSide
    width = Math.round(height * aspectRatio)
  } else {
    width = shortSide
    height = Math.round(width / aspectRatio)
  }

  // The height can grow to be bigger than the canvas height
  // We limit the height to 80% of the canvas height
  const { height: canvasHeight } = getCanvasDimensions()
  if (height > canvasHeight) {
    height = canvasHeight * HEIGHT_LIMIT_FACTOR
    width = height * aspectRatio
  }

  return { width, height }
}

export {
  calculateShapeDimensions,
  calculateUploadedFilePositionsAndDimensions,
  constrainShapePositionToCanvas,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_POSITION,
  DEFAULT_WIDTH,
  AUDIO_PLAYER_NO_IMAGE_VISUALIZER_ASPECT_RATIO,
  deleteFailedUploads,
  getDefaultWidthForUploadingShape,
  HEIGHT_LIMIT_FACTOR,
  MAXIMUM_NUMBER_OF_FILES,
  throwErrorForMissingDataInNewestPost,
}
